// Keeps the webchat widget off the critical path (the whole point of
// self-hosting it instead of the deferred-but-still-third-party Jivo). Renders
// nothing until first idle / first interaction, mirroring the exact deferral
// technique `+onRenderHtml.tsx` already uses for Jivo/Clarity, then lazy-loads
// the widget bundle so its JS only downloads once the trigger fires.

import { lazy, Suspense, useEffect, useState } from "react";

const LazyWebChatWidget = lazy(() => import("./WebChatWidget"));

// Rollout gate: the widget stays dark until the @lc/webchat backend is deployed
// and nginx routes /chat/* to it. Enable per-build with PUBLIC_WEBCHAT_ENABLED=1
// (Vite PUBLIC_ prefix, baked via vite.config.ts `define`). Unset / any other value
// = OFF, so a build can't ship a backend-less chat bubble by default.
const WEBCHAT_ENABLED = import.meta.env.PUBLIC_WEBCHAT_ENABLED === "1";

// Mirrors the old Jivo NO_JIVO_PAGE_NAMES opt-out (+onRenderHtml.tsx) — a page
// whose single action is a registration form shouldn't compete with a chat
// bubble.
export const NO_WEBCHAT_PAGE_NAMES = new Set(["webinars/top-8-resume-errors"]);

const DEFER_EVENTS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
];
const IDLE_TIMEOUT_MS = 5000;
const FALLBACK_DELAY_MS = 3000;

interface WebChatBootProps {
  pageName?: string;
}

export function WebChatBoot({ pageName }: WebChatBootProps) {
  const [boot, setBoot] = useState(false);
  const suppressed =
    !WEBCHAT_ENABLED || (typeof pageName === "string" && NO_WEBCHAT_PAGE_NAMES.has(pageName));

  useEffect(() => {
    if (typeof window === "undefined" || suppressed) return;

    let loaded = false;

    function load() {
      if (loaded) return;
      loaded = true;
      for (const event of DEFER_EVENTS) {
        window.removeEventListener(event, load);
      }
      setBoot(true);
    }

    for (const event of DEFER_EVENTS) {
      window.addEventListener(event, load, { once: true, passive: true });
    }

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(load, { timeout: IDLE_TIMEOUT_MS });
    } else {
      timeoutId = setTimeout(load, FALLBACK_DELAY_MS);
    }

    return () => {
      for (const event of DEFER_EVENTS) {
        window.removeEventListener(event, load);
      }
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [suppressed]);

  if (typeof window === "undefined" || suppressed || !boot) return null;

  return (
    <Suspense fallback={null}>
      <LazyWebChatWidget />
    </Suspense>
  );
}

WebChatBoot.displayName = "WebChatBoot";
